import { useState, useEffect, useCallback } from 'react';
import {
  suppliesApi,
  Supply,
  CreateSupplyDto,
  UpdateSupplyDto,
  GetSuppliesDto,
  SupplyStats,
  UpdateSupplyQuantityDto,
  SupplyAuditDto,
  SupplyStatus,
  SupplyType,
} from '../api/supplies';

interface UseSuppliesReturn {
  supplies: Supply[];
  loading: boolean;
  error: string | null;
  statistics: SupplyStats;
  createSupply: (data: CreateSupplyDto) => Promise<Supply | null>;
  updateSupply: (id: string, data: UpdateSupplyDto) => Promise<Supply | null>;
  deleteSupply: (id: string) => Promise<boolean>;
  updateSupplyQuantity: (id: string, data: UpdateSupplyQuantityDto) => Promise<Supply | null>;
  auditSupply: (id: string, data: SupplyAuditDto) => Promise<Supply | null>;
  refreshData: () => Promise<void>;
  loadSupplies: (filters?: GetSuppliesDto) => Promise<void>;
  getSupplyById: (id: string) => Promise<Supply | null>;
  getSuppliesByType: (type: SupplyType) => Promise<Supply[]>;
  getLowStockSupplies: () => Promise<Supply[]>;
  getExpiredSupplies: () => Promise<Supply[]>;
  getExpiringSoonSupplies: () => Promise<Supply[]>;
  // Utility functions
  getSupplyStatus: (supply: Supply) => SupplyStatus;
  isExpired: (supply: Supply) => boolean;
  isExpiringSoon: (supply: Supply) => boolean;
  getStockLevel: (supply: Supply) => 'adequate' | 'low' | 'out' | 'overstocked';
  getDaysUntilExpiry: (supply: Supply) => number | null;
}

const initialStats: SupplyStats = {
  totalItems: 0,
  lowStockItems: 0,
  outOfStockItems: 0,
  expiredItems: 0,
  expiringSoonItems: 0,
  totalValue: 0,
  overstockedItems: 0,
  averageDaysUntilExpiry: 0,
  itemsByType: {} as Record<SupplyType, number>,
  itemsByStatus: {} as Record<SupplyStatus, number>,
  topSuppliers: [],
  topLocations: [],
};

export const useSupplies = (): UseSuppliesReturn => {
  const [supplies, setSupplies] = useState<Supply[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statistics, setStatistics] = useState<SupplyStats>(initialStats);

  // Load supplies with optional filters
  const loadSupplies = useCallback(async (filters: GetSuppliesDto = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await suppliesApi.getSupplies(filters);
      setSupplies(response.supplies);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load supplies');
      console.error('Error loading supplies:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load statistics
  const loadStatistics = useCallback(async () => {
    try {
      const stats = await suppliesApi.getStatistics();
      setStatistics(stats);
    } catch (err) {
      console.error('Error loading statistics:', err);
    }
  }, []);

  // Refresh all data
  const refreshData = useCallback(async () => {
    await Promise.all([loadSupplies(), loadStatistics()]);
  }, [loadSupplies, loadStatistics]);

  // Create supply
  const createSupply = useCallback(async (data: CreateSupplyDto): Promise<Supply | null> => {
    try {
      setError(null);
      const newSupply = await suppliesApi.createSupply(data);
      setSupplies(prev => [newSupply, ...prev]);
      await loadStatistics(); // Refresh statistics
      return newSupply;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create supply');
      console.error('Error creating supply:', err);
      return null;
    }
  }, [loadStatistics]);

  // Update supply
  const updateSupply = useCallback(async (id: string, data: UpdateSupplyDto): Promise<Supply | null> => {
    try {
      setError(null);
      const updatedSupply = await suppliesApi.updateSupply(id, data);
      setSupplies(prev => prev.map(supply => 
        supply.id === id ? updatedSupply : supply
      ));
      await loadStatistics(); // Refresh statistics
      return updatedSupply;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update supply');
      console.error('Error updating supply:', err);
      return null;
    }
  }, [loadStatistics]);

  // Delete supply
  const deleteSupply = useCallback(async (id: string): Promise<boolean> => {
    try {
      setError(null);
      await suppliesApi.deleteSupply(id);
      setSupplies(prev => prev.filter(supply => supply.id !== id));
      await loadStatistics(); // Refresh statistics
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete supply');
      console.error('Error deleting supply:', err);
      return false;
    }
  }, [loadStatistics]);

  // Update supply quantity
  const updateSupplyQuantity = useCallback(async (id: string, data: UpdateSupplyQuantityDto): Promise<Supply | null> => {
    try {
      setError(null);
      const updatedSupply = await suppliesApi.updateSupplyQuantity(id, data);
      setSupplies(prev => prev.map(supply => 
        supply.id === id ? updatedSupply : supply
      ));
      await loadStatistics(); // Refresh statistics
      return updatedSupply;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update supply quantity');
      console.error('Error updating supply quantity:', err);
      return null;
    }
  }, [loadStatistics]);

  // Audit supply
  const auditSupply = useCallback(async (id: string, data: SupplyAuditDto): Promise<Supply | null> => {
    try {
      setError(null);
      const auditedSupply = await suppliesApi.auditSupply(id, data);
      setSupplies(prev => prev.map(supply => 
        supply.id === id ? auditedSupply : supply
      ));
      await loadStatistics(); // Refresh statistics
      return auditedSupply;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to audit supply');
      console.error('Error auditing supply:', err);
      return null;
    }
  }, [loadStatistics]);

  // Get supply by ID
  const getSupplyById = useCallback(async (id: string): Promise<Supply | null> => {
    try {
      setError(null);
      return await suppliesApi.getSupply(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get supply');
      console.error('Error getting supply:', err);
      return null;
    }
  }, []);

  // Get supplies by type
  const getSuppliesByType = useCallback(async (type: SupplyType): Promise<Supply[]> => {
    try {
      setError(null);
      return await suppliesApi.getSuppliesByType(type);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get supplies by type');
      console.error('Error getting supplies by type:', err);
      return [];
    }
  }, []);

  // Get low stock supplies
  const getLowStockSupplies = useCallback(async (): Promise<Supply[]> => {
    try {
      setError(null);
      return await suppliesApi.getLowStockSupplies();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get low stock supplies');
      console.error('Error getting low stock supplies:', err);
      return [];
    }
  }, []);

  // Get expired supplies
  const getExpiredSupplies = useCallback(async (): Promise<Supply[]> => {
    try {
      setError(null);
      return await suppliesApi.getExpiredSupplies();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get expired supplies');
      console.error('Error getting expired supplies:', err);
      return [];
    }
  }, []);

  // Get expiring soon supplies
  const getExpiringSoonSupplies = useCallback(async (): Promise<Supply[]> => {
    try {
      setError(null);
      return await suppliesApi.getExpiringSoonSupplies();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get expiring soon supplies');
      console.error('Error getting expiring soon supplies:', err);
      return [];
    }
  }, []);

  // Utility functions
  const getSupplyStatus = useCallback((supply: Supply): SupplyStatus => {
    return supply.status;
  }, []);

  const isExpired = useCallback((supply: Supply): boolean => {
    return supply.isExpired;
  }, []);

  const isExpiringSoon = useCallback((supply: Supply): boolean => {
    return supply.isExpiringSoon;
  }, []);

  const getStockLevel = useCallback((supply: Supply): 'adequate' | 'low' | 'out' | 'overstocked' => {
    return supply.stockLevel;
  }, []);

  const getDaysUntilExpiry = useCallback((supply: Supply): number | null => {
    return supply.daysUntilExpiry || null;
  }, []);

  // Load initial data
  useEffect(() => {
    refreshData();
  }, [refreshData]);

  return {
    supplies,
    loading,
    error,
    statistics,
    createSupply,
    updateSupply,
    deleteSupply,
    updateSupplyQuantity,
    auditSupply,
    refreshData,
    loadSupplies,
    getSupplyById,
    getSuppliesByType,
    getLowStockSupplies,
    getExpiredSupplies,
    getExpiringSoonSupplies,
    getSupplyStatus,
    isExpired,
    isExpiringSoon,
    getStockLevel,
    getDaysUntilExpiry,
  };
}; 